import React, { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  TIMELINE_CATEGORY_COLORS,
  TIMELINE_CATEGORY_DOT_COLORS,
  TIMELINE_CATEGORY_LABELS,
  TimelineEvent,
  formatTimelineCircleDate,
  formatTimelineDateTime,
} from '../../utils/studentTimeline';

type Props = {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
};

const monthGroupLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris',
    month: 'long',
    year: 'numeric',
  });

const TIMELINE_RAIL_WIDTH = 56;

const StudentParcoursTimeline: React.FC<Props> = ({ events, onEventClick }) => {
  const sortedAsc = useMemo(
    () => [...events].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
    [events]
  );

  if (events.length === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={4}>
        Aucun événement à afficher pour le moment.
      </Typography>
    );
  }

  const now = Date.now();
  let currentMonth = '';
  let seenFuture = false;
  let todayMarkerShown = false;

  return (
    <Box sx={{ position: 'relative', pl: `${TIMELINE_RAIL_WIDTH + 16}px` }}>
      <Box
        sx={{
          position: 'absolute',
          left: TIMELINE_RAIL_WIDTH / 2 - 1,
          top: 28,
          bottom: 28,
          width: 2,
          bgcolor: 'grey.300',
          borderRadius: 1,
        }}
      />

      <Stack spacing={2}>
        {sortedAsc.map((event, index) => {
          const monthLabel = monthGroupLabel(event.startAt);
          const showMonthHeader = monthLabel !== currentMonth;
          if (showMonthHeader) {
            currentMonth = monthLabel;
          }

          const isFuture = new Date(event.startAt).getTime() > now;
          const showTodayMarker = !todayMarkerShown && seenFuture && !isFuture;
          if (isFuture) {
            seenFuture = true;
          }
          if (showTodayMarker) {
            todayMarkerShown = true;
          }

          const circleDate = formatTimelineCircleDate(event.startAt);

          return (
            <React.Fragment key={event.id}>
              {showMonthHeader && (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ textTransform: 'capitalize', mt: index === 0 ? 0 : 1 }}
                >
                  {monthLabel}
                </Typography>
              )}
              {showTodayMarker && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5, ml: `-${TIMELINE_RAIL_WIDTH + 16}px` }}>
                  <Chip size="small" label="Aujourd'hui" color="primary" variant="outlined" />
                </Box>
              )}
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: -(TIMELINE_RAIL_WIDTH + 16),
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: TIMELINE_RAIL_WIDTH,
                    height: TIMELINE_RAIL_WIDTH,
                    borderRadius: '50%',
                    bgcolor: TIMELINE_CATEGORY_DOT_COLORS[event.category],
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    lineHeight: 1.1,
                    boxShadow: 1,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    zIndex: 1,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.95rem', fontWeight: 800, lineHeight: 1 }}
                  >
                    {circleDate.day}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      textTransform: 'lowercase',
                      lineHeight: 1.1,
                      mt: 0.25,
                    }}
                  >
                    {circleDate.month}
                  </Typography>
                </Box>

                <Paper
                  variant="outlined"
                  onClick={() => onEventClick?.(event)}
                  sx={{
                    p: 1.5,
                    borderStyle: event.isFuture ? 'dashed' : 'solid',
                    cursor: onEventClick ? 'pointer' : 'default',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={TIMELINE_CATEGORY_COLORS[event.category]}
                      label={TIMELINE_CATEGORY_LABELS[event.category]}
                    />
                    {event.isFuture && <Chip size="small" variant="outlined" label="À venir" />}
                    {event.payload?.isAutomatic === true && (
                      <Chip size="small" variant="outlined" color="info" label="Automatique" />
                    )}
                  </Stack>
                  <Typography variant="body2" fontWeight={700}>
                    {event.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimelineDateTime(event.startAt)}
                  </Typography>
                  {event.description && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {event.description}
                    </Typography>
                  )}
                </Paper>
              </Box>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};

export default StudentParcoursTimeline;
